import type { CustomFieldType, LogicFilterValue } from '../../fetchSurvey/types/survey';
import { LOGIC_FIELDS } from '../constants/logicFields';
import { LOGIC_OPERATORS } from '../constants/logicOperators';
import { parseCompositeKey } from './parseCompositeKey';

const MATRIX_ANSWER_TYPES = new Set([
  'CFM_MATRIX',
  'CSAT_MATRIX',
  'RATING_MATRIX',
]);

function isExistsOrMissingOperator(operator: string): boolean {
  return operator === LOGIC_OPERATORS.EXISTS || operator === LOGIC_OPERATORS.MISSING;
}

function isContainsOperator(operator: string): boolean {
  return (
    operator === LOGIC_OPERATORS.CONTAINS || operator === LOGIC_OPERATORS.DOES_NOT_CONTAIN
  );
}

function inferFieldFromAnswerType(answerType: string): string | undefined {
  switch (answerType) {
    case 'MCQ':
      return LOGIC_FIELDS.SELECTED_OPTIONS;
    case 'TEXTFIELD':
      return LOGIC_FIELDS.TEXT;
    case 'NPS_SCALE':
    case 'SCALE':
      return LOGIC_FIELDS.TEN_POINT_SCALE;
    case 'CFM_MATRIX':
    case 'CSAT_MATRIX':
    case 'RATING_MATRIX':
      return LOGIC_FIELDS.MATRIX_OPTIONS;
    case 'SLIDER_MATRIX':
      return LOGIC_FIELDS.SLIDER;
    case 'FILE_UPLOAD':
      return LOGIC_FIELDS.FILE_UPLOAD;
    case 'HEATMAP':
      return LOGIC_FIELDS.HEATMAP;
    case 'RANK_ORDER':
      return LOGIC_FIELDS.RANK_ORDER;
    default:
      return undefined;
  }
}

function looksLikeHeatmapRegionValue(value: LogicFilterValue): boolean {
  if (typeof value !== 'string') {
    return false;
  }

  return !value.includes('_') || value.startsWith('Region ');
}

function inferFieldFromValues(
  answerType: string | undefined,
  operator: string,
  values: LogicFilterValue[] | undefined
): string | undefined {
  if (!answerType || !values?.length) {
    return undefined;
  }

  const firstValue = values[0];
  const compositeKey = parseCompositeKey(firstValue);

  if (answerType === 'HEATMAP' && isContainsOperator(operator)) {
    if (values.every(looksLikeHeatmapRegionValue)) {
      return LOGIC_FIELDS.HEATMAP_REGION;
    }
  }

  if (answerType === 'SLIDER_MATRIX' && compositeKey) {
    return LOGIC_FIELDS.SLIDER;
  }

  if (MATRIX_ANSWER_TYPES.has(answerType) && compositeKey) {
    return LOGIC_FIELDS.MATRIX_OPTIONS;
  }

  return undefined;
}

export function resolveQuestionLogicField(
  field: string | undefined,
  answerType: string | undefined,
  operator: string,
  values?: LogicFilterValue[]
): string | undefined {
  if (field) {
    return field;
  }

  if (!answerType) {
    return undefined;
  }

  const inferredFromValues = inferFieldFromValues(answerType, operator, values);
  if (inferredFromValues) {
    return inferredFromValues;
  }

  if (isExistsOrMissingOperator(operator)) {
    return inferFieldFromAnswerType(answerType);
  }

  return undefined;
}

function inferCustomFieldFromFieldType(fieldType: CustomFieldType): string | undefined {
  switch (fieldType) {
    case 'TEXT':
    case 'TEXT_MULTI':
    case 'TEXTAREA':
      return LOGIC_FIELDS.TEXT;
    case 'NUMBER':
      return LOGIC_FIELDS.NUMBER;
    case 'PICKLIST':
    case 'PICKLIST_MULTISELECT':
      return LOGIC_FIELDS.SELECTED_OPTIONS;
    case 'DATETIME':
      return LOGIC_FIELDS.SELECTED_DATETIME;
    default:
      return undefined;
  }
}

export function resolveCustomFieldLogicField(
  field: string | undefined,
  fieldType: CustomFieldType | undefined,
  operator: string
): string | undefined {
  if (field) {
    return field;
  }

  if (!fieldType) {
    return undefined;
  }

  if (isExistsOrMissingOperator(operator)) {
    return inferCustomFieldFromFieldType(fieldType);
  }

  return undefined;
}
